import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Loader2, Archive, BookOpen } from "lucide-react";
import { searchFreeEbooks, EbookSearchResult, preferredEbookSource } from "@/services/freeEbookService";
import FreeEbookModal from "./FreeEbookModal";
/* Lazy: a whole novel's worth of reader should not sit in the bundle for the
   ~95% of cards that have no public-domain edition. */
const BookReaderModal = lazy(() => import("./BookReaderModal"));
import gsap from "gsap";

interface FreeEbookDownloadIconProps {
  title: string;
  author: string;
  isbn?: string;
  className?: string;
  onAvailabilityChange?: (hasLinks: boolean) => void;
}

const FreeEbookDownloadIcon = ({ title, author, isbn, className = "", onAvailabilityChange }: FreeEbookDownloadIconProps) => {
  const [ebookData, setEbookData] = useState<EbookSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let isMounted = true;

    const searchForFreeEbooks = async () => {
      try {
        // Use cache by default for card-level icon; preview modal will force refresh
        const result = await searchFreeEbooks(title, author, isbn);
        if (isMounted) {
          setEbookData(result);
        }
      } catch (error) {
        console.error('Error searching for free ebooks:', error);
        if (isMounted) {
          setEbookData({ hasLinks: false });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    searchForFreeEbooks();

    return () => {
      isMounted = false;
    };
  }, [title, author, isbn]);

  // Add GSAP animation on mount - only entrance animation, no pulsing
  useEffect(() => {
    if (buttonRef.current && ebookData?.hasLinks) {
      gsap.from(buttonRef.current, {
        scale: 0.8,
        opacity: 0,
        duration: 0.5,
        ease: "back.out(1.7)",
      });
    }
  }, [ebookData?.hasLinks]);

  // Notify parent when availability changes
  useEffect(() => {
    if (onAvailabilityChange && !isLoading) {
      onAvailabilityChange(!!ebookData?.hasLinks);
    }
  }, [ebookData?.hasLinks, isLoading, onAvailabilityChange]);

  /**
   * A Gutenberg edition is readable in full, right here — so open the reader
   * rather than a modal whose only offer is a link off the site. Anything else
   * is an Internet Archive lending copy, which cannot be read inline (a borrow
   * wall inside a reader is worse than an honest outbound link), so that keeps
   * the existing modal.
   */
  const handleClick = () => {
    if (!ebookData?.hasLinks) return;
    if (ebookData.gutenberg?.id) {
      setIsReaderOpen(true);
      return;
    }
    setIsModalOpen(true);
  };

  // Show loading state with consistent sizing; keep space even if hidden later
  if (isLoading) {
    return (
      <button
        disabled
        className={`flex items-center justify-center px-2 py-1 bg-transparent border border-[rgba(255,255,255,0.1)] text-slate-400 text-xs rounded-md ${className}`}
        /* Neutral while loading — which source we found is not known yet, and
           naming one here made the chip flip label mid-render. */
        aria-label="Checking free ebook availability"
      >
        <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
        <span className="text-[10px]">Free ebook</span>
      </button>
    );
  }

  // Don't render if no links found
  if (!ebookData?.hasLinks) {
    return null;
  }

  /**
   * Name the source we actually found, rather than always saying "Internet
   * Archive".
   *
   * This label was hardcoded, so a book with a Project Gutenberg edition still
   * advertised the Archive — and the modal behind it (correctly) opens
   * Gutenberg first, so the chip described the wrong thing. Gutenberg wins the
   * billing when present because it is public domain and reads in full, while
   * most Archive holdings here are lending copies.
   */
  const preferred = preferredEbookSource(ebookData);
  const sourceLabel = preferred?.label ?? "Internet Archive";
  const SourceIcon = preferred?.kind === 'gutenberg' ? BookOpen : Archive;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleClick}
        className={`flex items-center justify-center px-2 py-1 bg-transparent border border-[rgba(255,255,255,0.1)] text-slate-400 text-xs rounded-md transition-all duration-300 ease-in-out hover:border-slate-300 hover:text-slate-200 ${className}`}
        title={`Read "${title}" on ${sourceLabel}`}
        aria-label={`Free ebook for ${title} on ${sourceLabel}`}
      >
        <SourceIcon className="w-3 h-3 mr-1.5 flex-shrink-0" />
        <span className="text-[10px] whitespace-nowrap">{sourceLabel}</span>
      </button>
      
      <FreeEbookModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
        author={author}
        ebookData={ebookData}
        isLoading={false}
      />

      {isReaderOpen && (
        <Suspense fallback={null}>
          <BookReaderModal
            isOpen={isReaderOpen}
            onClose={() => setIsReaderOpen(false)}
            gutenbergId={ebookData?.gutenberg?.id ?? null}
            title={title}
            author={author}
          />
        </Suspense>
      )}
    </>
  );
};

export default FreeEbookDownloadIcon;